/* ─────────────────────────────────────────────────────────────
   INVESTMENT THESIS TAB
   GET /api/v1/stocks/{ticker}/thesis
   (+ the already-live GET .../{ticker}/fair-value-chart for the IV / analyst
    rows the lens itself doesn't carry)

   Replicates the reference note's Investment-Thesis panel section by section:

     stance card        ← note.stance_label + note.stance (fallback narrative)
     one-liner          ← note.one_liner
     bull / bear        ← note.bull[] / note.bear[]   (point + evidence)
     what would change  ← note.change_mind_breaks[] / note.change_mind_confirms[]
       our mind
     valuation lens     ← valuation_lens (+ valuation_lens.reads for the note's
                          per-row "Read" column), extended with the fair-value
                          chart's IV/share, analyst target and street consensus

   The `note.*` sections come from the stored per-quarter earnings note and are
   null until the seeder has run for the stock — render what's present.

   `narrative` is generated server-side and cached ~4h, so repeat calls are
   cheap; it is deliberately NOT cached client-side beyond this session.
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getThesis, getFairValueChart } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";
import {
  ratingTone,
  signalTone,
  resolveBand,
  fmtNum,
  fmtPct,
  fmtDate,
  fmtPeg,
  fmtMultiple,
  fmtRank,
  SENTIMENT_DISCLAIMER,
} from "../../../config/earningsNote";
import {
  NotePage, SectionTitle, Card, Banner, Pill, GradeChip,
  Scroller, Table, Th, Td, Tl, NoteSkeleton, NoteEmpty, toneColor,
  NoteMasthead, NoteFooter, NoteList,
} from "../Widgets/earningsNoteUI";

/* ── Bull / bear column ───────────────────────────────────────── */
const CaseColumn = ({ kind, items }) => {
  const bull = kind === "bull";
  return (
    <Card
      accent={bull ? "var(--color-success)" : "var(--color-error)"}
      style={{ flex: 1, minWidth: 290 }}
    >
      <div
        style={{
          color: bull ? "var(--color-success)" : "var(--color-error)",
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-bold)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        {bull ? "Bull case" : "Bear case"}
      </div>
      <NoteList items={items} boldKey="point" bodyKeys={["evidence"]} />
    </Card>
  );
};

const Thesis = ({ stock }) => {
  const ticker = stock?.ticker;
  const [data, setData] = useState(null);
  const [fv, setFv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      // Fair value is a nice-to-have here — a failure there must not blank the tab.
      const [res, fvRes] = await Promise.all([getThesis(ticker), getFairValueChart(ticker)]);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setFv(fvRes?.success ? fvRes.data : null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[120, 200]} />;
  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data)
    return <NoteEmpty>Thesis data is not available for {ticker}.</NoteEmpty>;

  const {
    narrative,
    overall_rating,
    overall_grade,
    overall_score,
    rating_date,
    valuation_lens,
    note,
  } = data;

  const bull = Array.isArray(note?.bull) ? note.bull : [];
  const bear = Array.isArray(note?.bear) ? note.bear : [];
  const breaks = Array.isArray(note?.change_mind_breaks) ? note.change_mind_breaks : [];
  const confirms = Array.isArray(note?.change_mind_confirms) ? note.change_mind_confirms : [];

  /* Stance prose: the note's own stance, falling back to the generated narrative. */
  const stanceProse = note?.stance || narrative || null;
  const stanceLabel = note?.stance_label || overall_rating || null;

  const reads = valuation_lens?.reads ?? {};
  const mgmtBand = valuation_lens
    ? resolveBand(valuation_lens.mgmt_sentiment_band, valuation_lens.mgmt_sentiment_score)
    : null;

  /* ── Valuation lens — the note's "Valuation" table ────────────
     Rows come straight from valuation_lens; the "Read" column is the
     server-templated reads[key] for that row (absent key = no read).      */
  const lensRows = [];
  const pushLens = (row) => {
    if (row) lensRows.push(row);
  };

  if (valuation_lens) {
    const vl = valuation_lens;

    pushLens(
      vl.price != null && {
        key: "price",
        label: "Current price",
        value: formatCurrency(vl.price),
        tone: "neutral",
      }
    );
    pushLens(
      vl.fwd_pe != null && {
        key: "fwd_pe",
        label: "Forward P/E",
        value: fmtMultiple(vl.fwd_pe),
        tone: "neutral",
      }
    );
    pushLens(
      vl.peg != null && {
        key: "peg",
        label: "PEG",
        value: fmtPeg(vl.peg),
        flag: vl.peg_flag
          ? `PEG flattered by a ${vl.peg_flag} / peak-EPS surge — read with caution.`
          : null,
        tone: "neutral",
      }
    );
    pushLens(
      vl.fwd_ps != null && {
        key: "fwd_ps",
        label: "Forward P/S",
        value: fmtMultiple(vl.fwd_ps),
        tone: "neutral",
      }
    );
    pushLens(
      vl.model_rank != null && {
        key: "model_rank",
        label: "Factor-model rank",
        value: fmtRank(vl.model_rank, vl.model_universe),
        tone: "neutral",
      }
    );
    pushLens(
      vl.fair_value != null && {
        key: "fair_value",
        label: "Fair value",
        value: formatCurrency(vl.fair_value),
        tone:
          vl.upside_to_fv == null ? "neutral" : Number(vl.upside_to_fv) >= 0 ? "pos" : "neg",
        fallbackRead:
          vl.upside_to_fv != null ? `${fmtPct(vl.upside_to_fv, { decimals: 1 })} vs price` : null,
      }
    );
    pushLens(
      vl.mgmt_sentiment_score != null && {
        key: "mgmt_sentiment",
        label: "Management sentiment",
        value: `${fmtNum(vl.mgmt_sentiment_score, 1)}/100`,
        valueColor: mgmtBand?.color,
        tone: "neutral",
        fallbackRead: vl.mgmt_sentiment_band ? `${vl.mgmt_sentiment_band} (Earnings tab)` : null,
      }
    );
  }

  /* Rows the lens doesn't carry, from the fair-value chart endpoint. */
  if (fv) {
    pushLens(
      fv.fv_iv != null && {
        key: "fv_iv",
        label: "True value / share",
        value: formatCurrency(fv.fv_iv),
        tone: "neutral",
        fallbackRead: fv.iv_data_as_of ? `Data as of ${fmtDate(fv.iv_data_as_of)}` : null,
      }
    );
    pushLens(
      fv.fv_analyst != null && {
        key: "fv_analyst",
        label: "Analyst target",
        value: formatCurrency(fv.fv_analyst),
        tone: "neutral",
        fallbackRead:
          [
            fv.num_target_analysts_3m != null ? `${fv.num_target_analysts_3m} analysts (3m)` : null,
            fv.num_target_analysts_12m != null ? `${fv.num_target_analysts_12m} (12m)` : null,
          ]
            .filter(Boolean)
            .join(" · ") || null,
      }
    );
    pushLens(
      fv.signal && {
        key: "signal",
        label: "Fair-value signal",
        value: fv.signal,
        tone: signalTone(fv.signal),
      }
    );
    pushLens(
      fv.analyst_consensus && {
        key: "analyst_consensus",
        label: "Street consensus",
        value: fv.analyst_consensus,
        tone: "neutral",
      }
    );
  }

  const hasStance =
    stanceLabel || overall_grade || overall_score != null || stanceProse || note?.one_liner;

  if (!hasStance && lensRows.length === 0 && !bull.length && !bear.length)
    return <NoteEmpty>Thesis data is not available for {ticker}.</NoteEmpty>;

  return (
    <NotePage>
      {/* ── Masthead ── */}
      <NoteMasthead
        eyebrow="Investment thesis"
        title={`${stock?.company_name || data.ticker || ticker} (${data.ticker || ticker}) — Investment Thesis`}
        meta={[
          rating_date ? `rated ${fmtDate(rating_date)}` : null,
          valuation_lens?.price != null
            ? `price ${formatCurrency(valuation_lens.price)}`
            : fv?.price != null
            ? `price ${formatCurrency(fv.price)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      >
        {note?.stance_label && <Pill tone="info">{note.stance_label}</Pill>}
      </NoteMasthead>

      {/* ── Stance card ── */}
      {hasStance && (
        <Card
          accent={toneColor(ratingTone(overall_rating ?? stanceLabel))}
          style={{ marginTop: "var(--spacing-sm)" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
              flexWrap: "wrap",
              marginBottom: stanceProse || note?.one_liner ? "var(--spacing-sm)" : 0,
            }}
          >
            {stanceLabel && (
              <div>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  Stance
                </div>
                <div
                  style={{
                    color: toneColor(ratingTone(overall_rating ?? stanceLabel)),
                    fontSize: "var(--font-size-2xl)",
                    fontWeight: "var(--font-weight-black)",
                    lineHeight: 1.2,
                  }}
                >
                  {stanceLabel}
                </div>
                {note?.stance_label && overall_rating && note.stance_label !== overall_rating && (
                  <div
                    style={{
                      color: "var(--color-text-muted)",
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 500,
                    }}
                  >
                    Model rating: {overall_rating}
                  </div>
                )}
              </div>
            )}

            {overall_score != null && (
              <div>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  Overall score
                </div>
                <div
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-2xl)",
                    fontWeight: "var(--font-weight-bold)",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.2,
                  }}
                >
                  {fmtNum(overall_score, 2)}
                </div>
              </div>
            )}

            {overall_grade && (
              <div>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 4,
                  }}
                >
                  Grade
                </div>
                <GradeChip grade={overall_grade} />
              </div>
            )}

            {rating_date && (
              <div style={{ marginLeft: "auto" }}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 500,
                  }}
                >
                  Rated {fmtDate(rating_date)}
                </div>
              </div>
            )}
          </div>

          {note?.one_liner && (
            <div
              style={{
                color: "var(--color-text-primary)",
                fontSize: "var(--font-size-base)",
                fontWeight: "var(--font-weight-semibold)",
                fontStyle: "italic",
                lineHeight: 1.6,
                borderTop: "1px solid var(--color-border-primary)",
                paddingTop: "var(--spacing-sm)",
              }}
            >
              {note.one_liner}
            </div>
          )}

          {stanceProse && (
            <div
              style={{
                color: "var(--color-text-secondary)",
                fontSize: "var(--font-size-sm)",
                lineHeight: 1.7,
                borderTop: note?.one_liner ? undefined : "1px solid var(--color-border-primary)",
                paddingTop: note?.one_liner ? "var(--spacing-sm)" : "var(--spacing-sm)",
                marginTop: note?.one_liner ? 4 : 0,
              }}
            >
              {stanceProse}
            </div>
          )}
        </Card>
      )}

      {/* ── Bull / bear ── */}
      {(bull.length > 0 || bear.length > 0) && (
        <>
          <SectionTitle caption="point + supporting evidence">Bull vs bear</SectionTitle>
          <div style={{ display: "flex", gap: "var(--spacing-md)", flexWrap: "wrap" }}>
            {bull.length > 0 && <CaseColumn kind="bull" items={bull} />}
            {bear.length > 0 && <CaseColumn kind="bear" items={bear} />}
          </div>
        </>
      )}

      {/* ── What would change our mind ── */}
      {(breaks.length > 0 || confirms.length > 0) && (
        <>
          <SectionTitle caption="what breaks the thesis · what confirms it">
            What would change our mind
          </SectionTitle>
          <div style={{ display: "flex", gap: "var(--spacing-md)", flexWrap: "wrap" }}>
            {breaks.length > 0 && (
              <Card accent="var(--color-error)" style={{ flex: 1, minWidth: 290 }}>
                <div
                  style={{
                    color: "var(--color-error)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-bold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  Breaks the thesis
                </div>
                <NoteList items={breaks} />
              </Card>
            )}
            {confirms.length > 0 && (
              <Card accent="var(--color-success)" style={{ flex: 1, minWidth: 290 }}>
                <div
                  style={{
                    color: "var(--color-success)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-bold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  Confirms the thesis
                </div>
                <NoteList items={confirms} />
              </Card>
            )}
          </div>
        </>
      )}

      {/* ── Valuation lens ── */}
      {lensRows.length > 0 && (
        <>
          <SectionTitle caption="same formulas as the sector comps · forward, consensus-based">
            Valuation lens
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Lens</Th>
                  <Th>Value</Th>
                  <Th left style={{ minWidth: 260 }}>Read</Th>
                </tr>
              </thead>
              <tbody>
                {lensRows.map((r) => {
                  const read = reads[r.key] ?? r.fallbackRead ?? null;
                  return (
                    <tr key={r.key}>
                      <Tl>{r.label}</Tl>
                      <Td bold tone={r.tone} style={r.valueColor ? { color: r.valueColor } : undefined}>
                        {r.value}
                        {r.flag && (
                          <MuiTooltip title={r.flag} placement="top" arrow>
                            <span style={{ color: "var(--color-warning)", cursor: "help" }}> ⚠</span>
                          </MuiTooltip>
                        )}
                      </Td>
                      <Td left tone="muted" wrap>
                        {read ?? "—"}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>

          {valuation_lens?.mgmt_sentiment_score != null && (
            <Banner kind="plain" style={{ marginTop: "var(--spacing-sm)" }}>
              {SENTIMENT_DISCLAIMER}
            </Banner>
          )}
        </>
      )}

      <NoteFooter>
        The stance narrative is generated server-side from the current six-factor ratings and
        refreshed roughly every four hours; ratings refresh nightly, and fair value and true
        value nightly at 18:45 ET. The bull / bear cases and the “what would change our mind”
        triggers come from the per-quarter earnings note, written from the call transcript and the
        same computed numbers shown here. For internal analysis only — not investment advice.
      </NoteFooter>
    </NotePage>
  );
};

export default Thesis;
